import { useAtomValue } from 'jotai'
import { pick } from 'lodash-es'
import type { ComponentProps, ReactNode } from 'react'
import { Fragment, useMemo } from 'react'
import type { Currency } from '~/services/apollo/generated/graphql'
import { localeAtom } from '~/stores/locale'

function renderNonNumberParts(
  parts: Intl.NumberFormatPart[],
  renderCurrencyPart?: (renderProps: { value: string }) => ReactNode,
) {
  return parts.map((part, idx) =>
    part.type === 'currency' ? (
      <Fragment key={`${part.type}-${idx}`}>
        {renderCurrencyPart?.({ value: part.value }) ?? (
          <span data-part="currency">{part.value}</span>
        )}
      </Fragment>
    ) : (
      <Fragment key={`${part.type}-${idx}`}>{part.value}</Fragment>
    ),
  )
}

const NUMBER_PART_TYPES = new Set<Intl.NumberFormatPart['type']>([
  'fraction',
  'group',
  'integer',
  'decimal',
])

function getNumberPartBounds(parts: Intl.NumberFormatPart[]) {
  const firstNumberPartIndex = parts.findIndex(part =>
    NUMBER_PART_TYPES.has(part.type),
  )

  if (firstNumberPartIndex < 0) return undefined

  const lastNumberPartIndex = parts.findLastIndex(part =>
    NUMBER_PART_TYPES.has(part.type),
  )

  return { firstNumberPartIndex, lastNumberPartIndex }
}

function getNumberFormatOptions(formatter: Intl.NumberFormat) {
  return pick(formatter.resolvedOptions(), [
    'maximumFractionDigits',
    'minimumFractionDigits',
    'minimumIntegerDigits',
    'numberingSystem',
    'signDisplay',
    'useGrouping',
  ] as const)
}

interface PriceTagProps extends Omit<ComponentProps<'span'>, 'children'> {
  /**
   * Currency
   */
  currency: Currency
  /**
   * Price value (amount)
   */
  value: number
  /**
   * Custom renderer for the currency part
   * @defaults `<span data-part="currency">{currency}</span>`
   */
  renderCurrencyPart?: (renderProps: {
    /**
     * Currency code label
     */
    value: string
  }) => ReactNode
  /**
   * Custom renderer for the number part
   * @defaults `<span data-part="number">{formattedValue}</span>`
   */
  renderNumberPart?: (renderProps: {
    /**
     * Price value (amount)
     */
    value: number
    /**
     * Current locale
     */
    locale: string
    /**
     * Formatter options to render the currency-style number part without the
     * currency label
     */
    format: ReturnType<typeof getNumberFormatOptions>
    /**
     * Pre-formatted label of `value`
     */
    formatted: string
  }) => ReactNode
}

/**
 * A price tag label with built-in Intl API localization
 */
export default function PriceTag({
  currency,
  value,
  renderCurrencyPart,
  renderNumberPart,
  ...attrs
}: PriceTagProps) {
  const locale = useAtomValue(localeAtom)
  const formatter = useMemo(() => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
    })
  }, [locale, currency])
  const parts = useMemo(
    () => formatter.formatToParts(value),
    [formatter, value],
  )
  const numberPartBounds = useMemo(() => getNumberPartBounds(parts), [parts])
  const numberFormat = useMemo(
    () => getNumberFormatOptions(formatter),
    [formatter],
  )

  if (!numberPartBounds) {
    return (
      <span {...attrs}>{renderNonNumberParts(parts, renderCurrencyPart)}</span>
    )
  }

  const { firstNumberPartIndex, lastNumberPartIndex } = numberPartBounds
  const prefixParts = parts.slice(0, firstNumberPartIndex)
  const suffixParts = parts.slice(lastNumberPartIndex + 1)
  const formatted = parts
    .slice(firstNumberPartIndex, lastNumberPartIndex + 1)
    .map(part => part.value)
    .join('')

  return (
    <span {...attrs}>
      {renderNonNumberParts(prefixParts, renderCurrencyPart)}
      {renderNumberPart?.({
        value,
        locale,
        format: numberFormat,
        formatted,
      }) ?? <span data-part="number">{formatted}</span>}
      {renderNonNumberParts(suffixParts, renderCurrencyPart)}
    </span>
  )
}
